import { AIToolsBanner } from '@/components/features/ai-services/AIToolsBanner';
import { HireExpertsSidePanel } from '@/components/features/expertise/HireExpertsSidePanel';
import { motion } from 'framer-motion';
import React from 'react';
import { clsx } from 'clsx';

// Variants defined outside to avoid re-creation on each render.
const toolsVariants = {
  initial: {
    opacity: 0,
    x: 20,
    scale: 0.98,
  },
  animate: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      type: 'spring',
      stiffness: 250,
      damping: 35,
      mass: 1,
      delay: 0.2,
    },
  },
  exit: {
    opacity: 0,
    x: 20,
    scale: 0.98,
    transition: {
      duration: 0.25,
      ease: 'easeInOut',
    },
  },
};

interface RightSidebarProps {
  legalExperts: any[];
  isOpen?: boolean;
  showAIToolsBanner?: boolean;
  showHireExpertsSidePanel?: boolean;
}

// Right sidebar component, memoized for performance.
export const RightSidebar = React.memo(
  ({
    legalExperts,
    isOpen = true,
    showAIToolsBanner = true,
    showHireExpertsSidePanel = true,
  }: RightSidebarProps) => {
    return (
      <motion.div
        variants={toolsVariants}
        initial='initial'
        animate='animate'
        exit='exit'
        className={clsx(
          'flex h-full flex-col space-y-2 overflow-hidden border-l border-gray-200 bg-gray-50 shadow-lg dark:border-gray-700/50 dark:bg-gray-900/95 dark:shadow-gray-900/20 sm:space-y-3',
          'transition-[width] duration-200 ease-in-out',
          isOpen ? 'w-[380px]' : 'w-0',
          'flex-shrink-0'
        )}
      >
        {showAIToolsBanner && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className='flex-shrink-0 rounded-lg bg-white shadow-sm dark:bg-gray-800/50 dark:shadow-gray-900/10'
          >
            <AIToolsBanner />
          </motion.div>
        )}

        {showHireExpertsSidePanel && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className='flex-1 overflow-y-auto rounded-lg bg-white shadow-sm dark:bg-gray-800/50 dark:shadow-gray-900/10'
          >
            <HireExpertsSidePanel legalExperts={legalExperts} />
          </motion.div>
        )}
      </motion.div>
    );
  }
);

RightSidebar.displayName = 'RightSidebar';
